import React, { FunctionComponent, useContext, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Tabs, useIsBreakpointLargeAndUp, Text, Heading, Icon, Panel } from '@afterpaytouch/core'
import {
  useActiveBillingAgreementsDataSelector,
  useHasActiveBillingAgreement,
  useHasInactiveBillingAgreement,
  useInactiveBillingAgreementsDataSelector,
} from '../../state/billingAgreements/hooks'
import { StoreLogo } from '../StoreLogo'
import { Agreement, AgreementStatus } from '@afterpaytouch/portal-api/consumer/billingAgreements/types'
import clsx from 'clsx'
import Image from 'next/image'
import { BillingAgreementDetail } from '../BillingAgreementDetail'
import { RecurringPaymentsContext, RecurringPaymentTabs, RecurringPaymentTabsProps, RecurringPaymentsContextProvider } from './RecurringPaymentsContext'
import { useDisplayIllustrations } from '../../state/convergence/hooks'

const I18N_NAMESPACE = ['common', 'recurringPayments']
interface BillingAgreementMerchantRowProps {
  agreement: Agreement
  selectedAgreementId: number
  onAgreementSelect: (agreement: Agreement) => void
}

export const BillingAgreementMerchantRow: FunctionComponent<BillingAgreementMerchantRowProps> = ({ agreement, selectedAgreementId, onAgreementSelect }) => (
  <div
    data-testid={`${agreement.id}-merchant`}
    className={clsx(
      'hover:bg-dark-standard mt-4 flex  cursor-pointer items-center gap-x-4 rounded-[12px] px-4',
      selectedAgreementId === agreement.id && 'bg-dark-standard'
    )}
    onClick={() => onAgreementSelect(agreement)}
  >
    <StoreLogo merchant={agreement?.merchant} />
    <span className='flex-1'>
      <Heading lineClamp={1} data-testid={`${agreement.id}-merchant-name`}>
        {agreement.merchant?.name}
      </Heading>
    </span>
    {agreement.status === AgreementStatus.EXPIRED && (
      <span data-testid={`${agreement.id}-card-update`}>
        <Icon.CardUpdate size='ExtraSmall' role='img' title='Card update' />
      </span>
    )}
  </div>
)

// @ts-ignore: OPERATION BLEED STOPPER
export const NoBillingAgreement: FunctionComponent<{ displayText: string; isBpLargeUp: boolean; showInMobile: boolean }> = ({
  displayText,
  isBpLargeUp,
  showInMobile,
}) => {
  const isDisplayIllustrations = useDisplayIllustrations()
  return (
    (isBpLargeUp || showInMobile) && (
      <Panel>
        <Panel.Content>
          <div className='mt-5 text-center md:mt-10' data-testid='no-billing-agreement-show-panel'>
            {isDisplayIllustrations && (
              <div className='mb-10'>
                <Image
                  width={224}
                  height={200}
                  src={`/images/recurringPayment.svg`}
                  alt=''
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            )}

            <div className='mb-10'>
              <Heading size='M' bold testNameSpace='no-billing-agreement-show'>
                {displayText}
              </Heading>
            </div>
          </div>
        </Panel.Content>
      </Panel>
    )
  )
}

export const RecurringPaymentsListContent: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const isBpLargeUp = useIsBreakpointLargeAndUp()
  const { tabValue, setTabValue, selectedAgreementId, setSelectedAgreementId } = useContext(RecurringPaymentsContext)

  const handleTabChange = (value: RecurringPaymentTabsProps): void => {
    setTabValue(value)
    // @ts-ignore: OPERATION BLEED STOPPER
    setSelectedAgreementId(null)
  }
  const hasActiveBillingAgreement = useHasActiveBillingAgreement()
  const hasInactiveBillingAgreement = useHasInactiveBillingAgreement()
  const activeBillingAgreements = useActiveBillingAgreementsDataSelector()
  const inactiveBillingAgreements = useInactiveBillingAgreementsDataSelector()
  const [menuActive, setMenuActive] = useState<boolean>(true)
  const toggleMenu = (): void => setMenuActive(!menuActive)
  const getAgreementDetails = (agreementId: number): void => {
    setSelectedAgreementId(agreementId)
    setMenuActive(false)
  }
  const getDisplayText = (): string => {
    switch (tabValue) {
      case RecurringPaymentTabs.Active:
        return hasActiveBillingAgreement
          ? t('recurringPayments:noSubscriptions:selected')
          : isBpLargeUp
          ? t('recurringPayments:noSubscriptions:exist')
          : t('recurringPayments:noSubscriptions:active')
      case RecurringPaymentTabs.Inactive:
        return hasInactiveBillingAgreement
          ? t('recurringPayments:noSubscriptions:selected')
          : isBpLargeUp
          ? t('recurringPayments:noSubscriptions:exist')
          : t('recurringPayments:noSubscriptions:inactive')
    }
  }

  const showInMobile = (): boolean => {
    return tabValue === RecurringPaymentTabs.Active ? !hasActiveBillingAgreement : !hasInactiveBillingAgreement
  }

  const showMenu = (): boolean => {
    if (isBpLargeUp !== false) {
      return true
    }
    return menuActive
  }

  const renderList = (): JSX.Element => {
    return (
      <div className='grid lg:grid-cols-[30%,70%]'>
        {showMenu() && (
          <div className={clsx('px-4 py-6 lg:px-8')}>
            <div className='mb-4'>
              <Tabs value={tabValue} onChange={handleTabChange} kind='Pill' size='Medium' stretch>
                {Object.values(RecurringPaymentTabs).map((item) => (
                  <Tabs.Item key={item} value={item} testNameSpace={`recurringPayments-${item}`}>
                    {t(`recurringPayments:${item}`)}
                  </Tabs.Item>
                ))}
              </Tabs>
            </div>

            <Tabs.Panel selectedValue={tabValue} value={RecurringPaymentTabs.Active}>
              <div>
                {!hasActiveBillingAgreement && isBpLargeUp && (
                  <Text size='M' testNameSpace='no-active-agreement'>
                    {t('recurringPayments:noSubscriptions:active')}
                  </Text>
                )}
                {hasActiveBillingAgreement &&
                  activeBillingAgreements.map((agreement) => (
                    <React.Fragment key={agreement.id}>
                      <BillingAgreementMerchantRow
                        agreement={agreement}
                        selectedAgreementId={selectedAgreementId}
                        onAgreementSelect={() => getAgreementDetails(agreement.id)}
                      ></BillingAgreementMerchantRow>
                    </React.Fragment>
                  ))}
              </div>
            </Tabs.Panel>

            <Tabs.Panel selectedValue={tabValue} value={RecurringPaymentTabs.Inactive}>
              <div>
                {!hasInactiveBillingAgreement && isBpLargeUp && (
                  <Text size='M' testNameSpace='no-inactive-agreement'>
                    {t('recurringPayments:noSubscriptions:inactive')}
                  </Text>
                )}
                {hasInactiveBillingAgreement &&
                  inactiveBillingAgreements.map((agreement) => (
                    <React.Fragment key={agreement.id}>
                      <BillingAgreementMerchantRow
                        agreement={agreement}
                        selectedAgreementId={selectedAgreementId}
                        onAgreementSelect={() => getAgreementDetails(agreement.id)}
                      ></BillingAgreementMerchantRow>
                    </React.Fragment>
                  ))}
              </div>
            </Tabs.Panel>
          </div>
        )}

        <div className={clsx('lg:border-dark-standard px-4 py-6 lg:border-0 lg:border-l-2 lg:border-solid lg:px-8 lg:pl-8')}>
          {selectedAgreementId ? (
            <BillingAgreementDetail agreementId={selectedAgreementId} onMenuToggle={toggleMenu} menuActive={menuActive}></BillingAgreementDetail>
          ) : (
            // @ts-ignore: OPERATION BLEED STOPPER
            <NoBillingAgreement displayText={getDisplayText()} isBpLargeUp={isBpLargeUp} showInMobile={showInMobile()}></NoBillingAgreement>
          )}
        </div>
      </div>
    )
  }
  return renderList()
}

export const RecurringPaymentsList: FunctionComponent = () => {
  return (
    <RecurringPaymentsContextProvider>
      <RecurringPaymentsListContent></RecurringPaymentsListContent>
    </RecurringPaymentsContextProvider>
  )
}
